// MarqueeRibbon — slim, dark, infinitely-scrolling horizontal marquee
// designed to sit flush against the bottom edge of each property card.
//
// Visuals: dark `#1A1513` background, cream uppercase lettering with
// generous letter-spacing, gold `#D4A373` Amazigh-diamond separators
// between repeated phrases. The track contains TWO sequential copies of
// the phrase list so the CSS animation can translate it by exactly 50%
// and seamlessly loop without any visible jump.
//
// Hover behaviour: pauses the animation on desktop (the wrapper sets the
// CSS variable `--marquee-play-state` to `paused` on hover).

import React, { useMemo } from "react";

const DEFAULT_PHRASES = (name) => [name?.toUpperCase() || "", "BOOK WITH US"];

const MarqueeRibbon = ({
  phrases,
  name,
  speed = 30,
  testIdPrefix = "marquee-ribbon",
}) => {
  // Resolve the phrase list — either passed explicitly, or derived from
  // the hotel/bivouac name + a shared CTA. Falls back to a single static
  // line when nothing is supplied so the ribbon is never empty.
  const list = useMemo(() => {
    if (Array.isArray(phrases) && phrases.length) return phrases;
    return DEFAULT_PHRASES(name);
  }, [phrases, name]);

  if (!list.length) return null;

  // Render two identical groups inline so the keyframe `-50%` translate
  // wraps the second copy perfectly into the first one's slot.
  const renderGroup = (groupIndex) => (
    <span
      className="inline-flex items-center shrink-0"
      aria-hidden={groupIndex > 0 ? true : undefined}
    >
      {list.map((phrase, i) => (
        <span
          key={`g${groupIndex}-${i}-${phrase}`}
          className="inline-flex items-center shrink-0"
        >
          <span className="px-6 sm:px-8 text-[11px] sm:text-[12px] tracking-[0.32em] uppercase font-medium text-[#FDFBF7]/95 whitespace-nowrap">
            {phrase}
          </span>
          {/* Amazigh diamond separator between every phrase */}
          <span
            aria-hidden="true"
            className="inline-block w-[8px] h-[8px] rotate-45 bg-[#D4A373] shrink-0 opacity-90"
          />
        </span>
      ))}
    </span>
  );

  return (
    <div
      className="relative overflow-hidden bg-[#1A1513] border-t border-[#D4A373]/15 group/marquee"
      data-testid={testIdPrefix}
      role="presentation"
    >
      {/* Soft edge fades so the entry/exit of words feels intentional. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-10 z-[1] bg-gradient-to-r from-[#1A1513] to-transparent"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-10 z-[1] bg-gradient-to-l from-[#1A1513] to-transparent"
      />

      <div
        className="flex items-center py-2.5 marquee-track"
        style={{
          // Speed is the seconds taken for one full -50 % translation.
          // Larger value = slower scroll.
          animationDuration: `${Math.max(8, speed)}s`,
        }}
      >
        {renderGroup(0)}
        {renderGroup(1)}
      </div>
    </div>
  );
};

export default MarqueeRibbon;
